import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';
import EnhancedTable from '../../components/EnhancedTable';
import ProductBomDialog from './ProductBomDialog';
import { ajaxGetBoms } from '../../services/bomService';
import { priceFormat } from '../../helpers';

export default function ProductBomList({ productId }) {
  const [items, setItems] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedBom, setSelectedBom] = useState(null);

  const loadBoms = useCallback(() => {
    ajaxGetBoms({ ProductId: productId })
      .then((res) => {
        const data = res.data.map((item, index) => {
          const altManufacturers = item.alt_parts?.map(alt => alt.part_manufacturer).filter(Boolean).join('\n');
          const altPartNumbers = item.alt_parts?.map(alt => alt.part_number).filter(Boolean).join('\n');
          
          return {
            ...item,
            item_index: index + 1,
            part_manufacturer: [item.part_manufacturer, altManufacturers].filter(Boolean).join('\n'),
            part_number: [item.part_number, altPartNumbers].filter(Boolean).join('\n'),
          };
        });
        setItems(data);
      })
      .catch(() => {
        setItems([]);
      });
  }, [productId]);

  useEffect(() => {
    if (productId !== -1) {
      loadBoms();
    }
  }, [productId, loadBoms]);

  const handleAdd = () => {
    setSelectedBom(null);
    setOpenDialog(true);
  };

  const handleEdit = (bomId) => {
    setSelectedBom({ id: bomId });
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedBom(null);
  };

  const handleDialogSave = () => {
    handleDialogClose();
    loadBoms();
  };

  const columns = [
    {
      id: 'item_index',
      label: 'Item',
      minWidth: 20,
      sortable: true,
      format: (value) => <div style={{ textAlign: 'center' }}>{value}</div>,
    },
    {
      id: 'quantity',
      label: 'Qty',
      minWidth: 20,
      sortable: true,
      format: (value) => <div style={{ textAlign: 'center' }}>{value}</div>,
    },
    {
      id: 'ref',
      label: 'Reference',
      sortable: true
    },
    {
      id: 'schematic',
      label: 'Part in Schematic',
      sortable: true
    },
    {
      id: 'part_desc',
      label: 'Description',
      sortable: true
    },  
    {
      id: 'part_manufacturer',
      label: 'Manufacturer',
      sortable: true,
      format: (value) => (
        <div style={{ whiteSpace: 'pre-line' }}>{value}</div>
      )
    },
    {
      id: 'part_number',
      label: 'Manufacturer Part#',
      sortable: true,
      format: (value) => (
        <div style={{ whiteSpace: 'pre-line' }}>{value}</div>
      )
    },
    {
      id: 'avg_price',
      label: 'Unit Price',
      sortable: true,
      format: (value) => priceFormat(value, 2, 6)
    },
    {
      id: 'avg_total',
      label: 'Total Price',
      sortable: true,
      format: (value) => priceFormat(value, 2, 6),
      summary: (item, column) => item[column.id] || 0
    },
    {
      id: 'remark',
      label: 'Remarks',
      minWidth: 20,
      format: (value) => value ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Tooltip 
            title={
              <div style={{ whiteSpace: 'pre-line' }}>
                {value}
              </div>
            }
          >
            <InfoIcon color="action" />
          </Tooltip>
        </div>
      ) : null
    }
  ];

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
        >
          Add BOM
        </Button>
      </Box>

      <EnhancedTable
        border
        fullHeight
        stickyColumn
        fontSize="12px"
        columns={columns}
        items={items}
        onRowClick={handleEdit}
      />

      {openDialog && (
        <ProductBomDialog
          open={openDialog}
          onClose={handleDialogClose}
          onSave={handleDialogSave}
          bom={selectedBom}
          productId={productId}
        />
      )}
    </Box>
  );
}
